import React from "react"
import { Link, useLocation } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Database, Brain, ClipboardList, BarChart, Zap, Users, ArrowRight } from "lucide-react"
import { ScrollReveal } from "../ui/scroll-reveal"
import { Button } from "../ui/button"
import { EventCategory } from "../../lib/analytics"
import { safeTrackEvent } from "../../lib/analytics-utils"

const individualServices = [
  {
    id: "knowledge-engineering",
    title: "Knowledge Engineering",
    description: "Structure your institutional knowledge for AI consumption. We build knowledge graphs, semantic systems and retrieval architecture - the foundation for AI that genuinely understands your organisation.",
    icon: Database,
    href: "/services/knowledge-engineering"
  },
  {
    id: "partner-ai",
    title: "Partner AI",
    description: "A conversational intelligence layer trained on your institutional knowledge. Not a generic chatbot - a knowledgeable partner that understands your terminology, processes and quality standards.",
    icon: Brain,
    href: "/services/partner-ai"
  },
  {
    id: "operational-assessment",
    title: "Operational Assessment",
    description: "Systematic discovery of current-state challenges with root cause analysis. Understand where you are before transforming where you are going - the foundation for prioritising improvement initiatives.",
    icon: ClipboardList,
    href: "/services/operational-assessment"
  },
  {
    id: "ai-opportunity",
    title: "AI Opportunity Identification",
    description: "Cut through the hype. Identify where AI actually creates genuine business value for your organisation, with realistic ROI projections and clear complexity ratings.",
    icon: BarChart,
    href: "/services/ai-opportunity"
  }
]

const packages = [
  {
    id: "ai-kickstarter",
    title: "AI Kickstarter",
    description: "A fully trained Partner AI in 8 weeks. A focused programme that assesses a target area of your operations, builds the Knowledge Architecture and deploys AI that proves value before you commit to more.",
    icon: Zap,
    href: "/services/ai-kickstarter"
  },
  {
    id: "strategic-transformation",
    title: "Strategic Transformation",
    description: "Comprehensive AI transformation for organisations wanting deep, lasting change. Embedded partnership that builds enterprise-wide Knowledge Architecture and deploys Partner AI capability.",
    icon: Users,
    href: "/services/strategic-transformation"
  }
]

export function CoreServices() {
  const location = useLocation()

  const handleServiceClick = (serviceTitle: string, href: string) => {
    safeTrackEvent({
      category: EventCategory.Navigation,
      label: `nav_services_${serviceTitle.toLowerCase().replace(/ /g, '_')}`,
      context: {
        sourcePage: location.pathname,
        destinationPage: href,
        section: "services_grid"
      }
    })
  }

  return (
    <section className="relative py-20 scroll-mt-20">
      <div className="container relative space-y-16">
        {/* Individual Services Section */}
        <div className="space-y-8">
          <ScrollReveal>
            <div className="text-center space-y-4 max-w-3xl mx-auto">
              <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
                Individual Services
              </h2>
              <p className="text-lg text-muted-foreground font-light">
                Standalone capabilities you can engage independently
              </p>
            </div>
          </ScrollReveal>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {individualServices.map((service, index) => {
              const Icon = service.icon
              return (
                <ScrollReveal key={service.id} delay={index * 0.1}>
                  <Link
                    to={service.href}
                    onClick={() => handleServiceClick(service.title, service.href)}
                    className="block h-full"
                  >
                    <Card className="bg-background/95 shadow-sm h-full hover:border-primary/50 hover:bg-primary/5 transition-all group">
                      <CardHeader>
                        <div className="flex items-center gap-4 mb-2">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                            <Icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                          </div>
                          <CardTitle className="text-lg group-hover:text-primary transition-colors">
                            {service.title}
                          </CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <p className="text-muted-foreground text-sm">
                          {service.description}
                        </p>
                        <div className="flex items-center text-primary font-medium text-sm">
                          Learn more
                          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </ScrollReveal>
              )
            })}
          </div>
        </div>

        {/* Packages Section */}
        <div className="space-y-8">
          <ScrollReveal>
            <div className="text-center space-y-4 max-w-3xl mx-auto">
              <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
                Engagement Packages
              </h2>
              <p className="text-lg text-muted-foreground font-light">
                Structured programmes that combine our services into proven delivery models
              </p>
            </div>
          </ScrollReveal>

          <div className="grid gap-6 md:grid-cols-2 max-w-4xl mx-auto">
            {packages.map((pkg, index) => {
              const Icon = pkg.icon
              return (
                <ScrollReveal key={pkg.id} delay={index * 0.1}>
                  <Link
                    to={pkg.href}
                    onClick={() => handleServiceClick(pkg.title, pkg.href)}
                    className="block h-full"
                  >
                    <Card className="bg-primary/5 border-primary/20 shadow-sm h-full hover:border-primary/50 hover:bg-primary/10 transition-all group">
                      <CardHeader>
                        <div className="flex items-center gap-4 mb-2">
                          <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                            <Icon className="w-6 h-6 text-primary-foreground" strokeWidth={1.5} />
                          </div>
                          <CardTitle className="group-hover:text-primary transition-colors">
                            {pkg.title}
                          </CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <p className="text-muted-foreground">
                          {pkg.description}
                        </p>
                        <div className="flex items-center text-primary font-medium">
                          Learn more
                          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </ScrollReveal>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
